import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Megaphone, Trash2, ChevronDown, Loader2 } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR") : "");

export function NoticesAdmin() {
  const [open, setOpen] = useState(false);
  const [notices, setNotices] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [shops, setShops] = useState([]);
  const [f, setF] = useState({ title: "", body: "", expires_at: "", shop_ids: [] });
  const [saving, setSaving] = useState(false);
  const load = async () => { try { const { data } = await api.get("/admin/notices"); setNotices(data); } catch (e) { toast.error(formatApiError(e)); } };
  useEffect(() => { load(); api.get("/shops").then(({ data }) => setShops(data)).catch(() => {}); }, []);
  const create = async () => {
    if (!f.title.trim() || !f.body.trim()) { toast.error("Título e texto são obrigatórios"); return; }
    setSaving(true);
    try {
      await api.post("/admin/notices", { title: f.title, body: f.body, expires_at: f.expires_at || null, shop_ids: f.shop_ids.length ? f.shop_ids : null });
      toast.success("Aviso enviado"); setDialog(false); setF({ title: "", body: "", expires_at: "", shop_ids: [] }); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const del = async (n) => { if (!window.confirm(`Excluir aviso "${n.title}"?`)) return; try { await api.delete(`/admin/notices/${n.id}`); setNotices((p) => p.filter((x) => x.id !== n.id)); } catch (e) { toast.error(formatApiError(e)); } };
  const toggle = (sid) => setF((p) => ({ ...p, shop_ids: p.shop_ids.includes(sid) ? p.shop_ids.filter((x) => x !== sid) : [...p.shop_ids, sid] }));
  return (
    <section data-testid="notices-admin" className="space-y-3">
      <button type="button" data-testid="notices-admin-toggle" onClick={() => setOpen((o) => !o)}
              className="w-full flex items-center justify-between gap-2 rounded-xl border border-zinc-800 bg-zinc-950/60 p-3 hover:bg-zinc-900">
        <span className="inline-flex items-center gap-2 font-display text-lg font-black uppercase text-zinc-100"><Megaphone className="h-5 w-5 text-amber-300" /> Avisos para oficinas <span className="text-xs text-zinc-400">({notices.length})</span></span>
        <ChevronDown className={`h-5 w-5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-3">
          <Button data-testid="notice-new" onClick={() => setDialog(true)} className="h-9 bg-green-brand hover:bg-green-brandDark text-white font-bold">Novo aviso</Button>
          <ul className="space-y-2">
            {notices.length === 0 && <li className="text-xs text-zinc-500">Nenhum aviso.</li>}
            {notices.map((n) => (
              <li key={n.id} data-testid={`admin-notice-${n.id}`} className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-3 flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-zinc-100">{n.title}</div>
                  <p className="text-xs text-zinc-400 whitespace-pre-wrap">{n.body}</p>
                  <div className="text-[11px] text-zinc-500 mt-1">{fmt(n.created_at)} · {n.expires_at ? `expira ${n.expires_at}` : "sem expiração"} · {n.shop_ids?.length ? `${n.shop_ids.length} oficina(s)` : "todas"}</div>
                </div>
                <Button data-testid={`notice-del-${n.id}`} variant="ghost" size="icon" onClick={() => del(n)} className="h-8 w-8 text-red-400 hover:bg-red-950/40"><Trash2 className="h-3.5 w-3.5" /></Button>
              </li>
            ))}
          </ul>
          <Dialog open={dialog} onOpenChange={(o) => !o && setDialog(false)}>
            <DialogContent data-testid="notice-dialog" className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-lg">
              <DialogHeader><DialogTitle className="font-display uppercase">Novo aviso</DialogTitle></DialogHeader>
              <div className="space-y-2">
                <Input data-testid="notice-title" placeholder="Título" value={f.title} onChange={(e) => setF((p) => ({ ...p, title: e.target.value }))} className="h-10 bg-zinc-900 border-zinc-800" />
                <Textarea data-testid="notice-body" placeholder="Texto" value={f.body} onChange={(e) => setF((p) => ({ ...p, body: e.target.value }))} className="min-h-[120px] bg-zinc-900 border-zinc-800" />
                <Input data-testid="notice-expires" type="date" value={f.expires_at} onChange={(e) => setF((p) => ({ ...p, expires_at: e.target.value }))} className="h-10 bg-zinc-900 border-zinc-800" />
                <div className="rounded-lg border border-zinc-800 p-2 max-h-48 overflow-y-auto">
                  <div className="text-[11px] text-zinc-400 mb-1">Deixe vazio para enviar a <strong>todas</strong>:</div>
                  {shops.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 text-sm py-1">
                      <input type="checkbox" data-testid={`notice-shop-${s.id}`} checked={f.shop_ids.includes(s.id)} onChange={() => toggle(s.id)} />
                      <span className="truncate">{s.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setDialog(false)} className="bg-zinc-900 border-zinc-800">Cancelar</Button>
                <Button data-testid="notice-send" onClick={create} disabled={saving} className="bg-green-brand hover:bg-green-brandDark text-white font-bold">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Enviar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </section>
  );
}
